import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

// Local components
import { TwoFactorCard } from './TwoFactorCard';
import { AuthenticatorSetupDialog } from './AuthenticatorSetupDialog';
import { SmsSetupDialog } from './SmsSetupDialog';
import { DisableAuthDialog, RegenerateBackupDialog, BackupCodesDisplayDialog } from './ActionDialogs';
import { API_URL } from './constants';

export default function TwoFactorSettings({ user }) {
  // Status state
  const [status, setStatus] = useState({ 
    totp_enabled: false, 
    sms_enabled: false,
    backup_codes_remaining: 0,
    phone_number: null 
  });
  const [smsAvailable, setSmsAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Dialog open states
  const [setupDialogOpen, setSetupDialogOpen] = useState(false);
  const [smsSetupDialogOpen, setSmsSetupDialogOpen] = useState(false);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [backupCodesDialogOpen, setBackupCodesDialogOpen] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  
  // Authenticator setup state
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [setupStep, setSetupStep] = useState('qr');
  const [backupCodes, setBackupCodes] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  
  // SMS setup state
  const [smsStep, setSmsStep] = useState('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  
  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [regenerateCode, setRegenerateCode] = useState('');

  useEffect(() => {
    fetchStatus();
    checkSmsAvailability();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/auth/2fa/status`, { withCredentials: true });
      setStatus(res.data);
    } catch {
      console.error('Failed to fetch 2FA status');
    } finally {
      setLoading(false);
    }
  };

  const checkSmsAvailability = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/2fa/sms-status`, { withCredentials: true });
      setSmsAvailable(res.data.sms_available);
    } catch {
      setSmsAvailable(false);
    }
  };

  // Authenticator setup handlers
  const handleSetup = async () => {
    try {
      setActionLoading(true);
      setError('');
      const res = await axios.post(`${API_URL}/api/auth/2fa/setup`, {}, { withCredentials: true });
      setSetupData(res.data);
      setSetupStep('qr');
      setSetupDialogOpen(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke starte 2FA-oppsett');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifySetup = async () => {
    try {
      setActionLoading(true);
      setError('');
      const res = await axios.post(`${API_URL}/api/auth/2fa/verify-setup`, 
        { code: verifyCode.replace(/\s/g, '') }, 
        { withCredentials: true }
      );
      setBackupCodes(res.data.backup_codes);
      setSetupStep('backup');
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ugyldig kode. Prøv igjen.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetupComplete = () => {
    setSetupDialogOpen(false);
    setSetupStep('qr');
    setVerifyCode('');
    setSetupData(null);
    setBackupCodes([]);
  };

  // SMS setup handlers
  const handleSmsSetup = () => {
    setSmsStep('phone');
    setPhoneNumber('');
    setSmsCode('');
    setError('');
    setSmsSetupDialogOpen(true);
  };

  const handleSendSmsCode = async () => {
    try {
      setActionLoading(true);
      setError('');
      
      let formattedPhone = phoneNumber.trim();
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+' + formattedPhone;
      }
      
      await axios.post(`${API_URL}/api/auth/2fa/phone/setup`, 
        { phone_number: formattedPhone }, 
        { withCredentials: true }
      );
      setPhoneNumber(formattedPhone);
      setSmsStep('verify');
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke sende SMS. Sjekk telefonnummeret.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifySms = async () => {
    try {
      setActionLoading(true);
      setError('');
      await axios.post(`${API_URL}/api/auth/2fa/phone/verify`, 
        { phone_number: phoneNumber, code: smsCode.trim() }, 
        { withCredentials: true }
      );
      setSmsSetupDialogOpen(false);
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ugyldig kode. Prøv igjen.');
    } finally {
      setActionLoading(false);
    }
  };

  // Disable and regenerate handlers
  const handleDisable = async () => {
    try {
      setActionLoading(true);
      setError('');
      await axios.post(`${API_URL}/api/auth/2fa/disable`, 
        { code: disableCode.replace(/\s/g, '') }, 
        { withCredentials: true }
      );
      setDisableDialogOpen(false);
      setDisableCode('');
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ugyldig kode. Kunne ikke deaktivere 2FA.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    try {
      setActionLoading(true);
      setError('');
      const res = await axios.post(`${API_URL}/api/auth/2fa/regenerate-backup-codes`, 
        { code: regenerateCode.replace(/\s/g, '') }, 
        { withCredentials: true }
      );
      setBackupCodes(res.data.backup_codes);
      setRegenerateDialogOpen(false);
      setBackupCodesDialogOpen(true);
      setRegenerateCode('');
      fetchStatus();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ugyldig kode. Kunne ikke generere nye backup-koder.');
    } finally {
      setActionLoading(false);
    }
  };

  // Utility handlers
  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAllBackupCodes = () => {
    const allCodes = backupCodes.join('\n');
    navigator.clipboard.writeText(allCodes);
    setCopiedIndex('all');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const clearError = () => setError('');

  if (loading) {
    return (
      <Card className="border-2">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <TwoFactorCard
        status={status}
        smsAvailable={smsAvailable}
        actionLoading={actionLoading}
        error={error}
        onSetupAuthenticator={handleSetup}
        onSetupSms={handleSmsSetup}
        onOpenRegenerateDialog={() => setRegenerateDialogOpen(true)}
        onOpenDisableDialog={() => setDisableDialogOpen(true)}
      />

      <AuthenticatorSetupDialog
        open={setupDialogOpen}
        onOpenChange={setSetupDialogOpen}
        setupStep={setupStep}
        setSetupStep={setSetupStep}
        setupData={setupData}
        verifyCode={verifyCode}
        setVerifyCode={setVerifyCode}
        backupCodes={backupCodes}
        actionLoading={actionLoading}
        error={error}
        copiedIndex={copiedIndex}
        onVerifySetup={handleVerifySetup}
        onCopyToClipboard={copyToClipboard}
        onCopyAllBackupCodes={copyAllBackupCodes}
        onComplete={handleSetupComplete}
      />

      <SmsSetupDialog
        open={smsSetupDialogOpen}
        onOpenChange={setSmsSetupDialogOpen}
        smsStep={smsStep}
        setSmsStep={setSmsStep}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        smsCode={smsCode}
        setSmsCode={setSmsCode}
        actionLoading={actionLoading}
        error={error}
        onSendSmsCode={handleSendSmsCode}
        onVerifySms={handleVerifySms}
      />

      <DisableAuthDialog
        open={disableDialogOpen}
        onOpenChange={setDisableDialogOpen}
        disableCode={disableCode}
        setDisableCode={setDisableCode}
        actionLoading={actionLoading}
        error={error}
        onDisable={handleDisable}
        onClearError={clearError}
      />

      <RegenerateBackupDialog
        open={regenerateDialogOpen}
        onOpenChange={setRegenerateDialogOpen}
        regenerateCode={regenerateCode}
        setRegenerateCode={setRegenerateCode}
        actionLoading={actionLoading}
        error={error}
        onRegenerate={handleRegenerateBackupCodes}
        onClearError={clearError}
      />

      <BackupCodesDisplayDialog
        open={backupCodesDialogOpen}
        onOpenChange={(open) => {
          setBackupCodesDialogOpen(open);
          if (!open) setBackupCodes([]);
        }}
        backupCodes={backupCodes}
        copiedIndex={copiedIndex}
        onCopyToClipboard={copyToClipboard}
        onCopyAllBackupCodes={copyAllBackupCodes}
      />
    </>
  );
}
